import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import api, { formatApiErrorDetail } from "../lib/api";
import { Analytics } from "../lib/analytics";
import { CheckCircle2, Send, BookOpen, Phone } from "lucide-react";

/**
 * LeadCapture — two variants:
 *   variant="consultation"  →  Book Free Consultation
 *   variant="syllabus"      →  Request Syllabus
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <button onClick={() => setOpen(true)}>Book</button>
 *   <LeadCapture variant="consultation" open={open} onClose={() => setOpen(false)} />
 */
export default function LeadCapture({ variant = "consultation", open, onClose, courseName = "" }) {
  const isConsultation = variant === "consultation";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: courseName,
    message: "",
  });
  const [busy, setBusy]   = useState(false);
  const [done, setDone]   = useState(false);
  const [err,  setErr]    = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Sync interest field when the parent provides a new course name
  useEffect(() => {
    if (courseName) setForm((f) => ({ ...f, interest: courseName }));
  }, [courseName]);

  const handleClose = () => {
    setDone(false);
    setErr("");
    onClose?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/leads", { ...form, source: variant });
      Analytics.leadSubmitted(variant);
      setDone(true);
    } catch (er) {
      setErr(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      eyebrow={isConsultation ? "Free 20-minute call" : "Course information"}
      title={isConsultation ? "Book a free consultation" : "Request the syllabus"}
      maxWidth="max-w-lg"
    >
      {done ? (
        <div className="flex flex-col items-center gap-4 py-6 text-center">
          <CheckCircle2 size={44} className="text-eduhu-success" />
          <p className="font-serif text-2xl text-eduhu-dark">
            {isConsultation ? "We'll be in touch shortly!" : "Syllabus on its way!"}
          </p>
          <p className="text-eduhu-inkSoft text-sm max-w-sm">
            {isConsultation
              ? "Our team will contact you within one business day to schedule your free consultation call."
              : "Check your inbox — we've sent the course syllabus to your email address."}
          </p>
          <button onClick={handleClose} className="eduhu-btn-primary mt-2">Done</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {isConsultation && (
            <div className="flex items-start gap-3 p-4 bg-eduhu-bg2 border border-eduhu-border text-sm text-eduhu-inkSoft leading-relaxed">
              <Phone size={16} className="text-eduhu-gold shrink-0 mt-0.5" />
              A free 20-minute call with one of our advisors. We'll help you pick the right course for your goals and answer any questions.
            </div>
          )}
          {!isConsultation && (
            <div className="flex items-start gap-3 p-4 bg-eduhu-bg2 border border-eduhu-border text-sm text-eduhu-inkSoft leading-relaxed">
              <BookOpen size={16} className="text-eduhu-gold shrink-0 mt-0.5" />
              We'll email you the full syllabus — topics covered, session structure, prerequisites and time commitment.
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <div className="overline text-eduhu-inkSoft mb-1">Full name *</div>
              <input required value={form.name} onChange={(e) => set("name", e.target.value)}
                className="w-full bg-eduhu-bg2 border border-eduhu-border px-3 py-2.5 text-sm focus:outline-none focus:border-eduhu-dark" />
            </label>
            <label className="block">
              <div className="overline text-eduhu-inkSoft mb-1">Email *</div>
              <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                className="w-full bg-eduhu-bg2 border border-eduhu-border px-3 py-2.5 text-sm focus:outline-none focus:border-eduhu-dark" />
            </label>
          </div>

          {isConsultation && (
            <label className="block">
              <div className="overline text-eduhu-inkSoft mb-1">Phone (optional)</div>
              <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                placeholder="+1 234 567 8900"
                className="w-full bg-eduhu-bg2 border border-eduhu-border px-3 py-2.5 text-sm focus:outline-none focus:border-eduhu-dark" />
            </label>
          )}

          <label className="block">
            <div className="overline text-eduhu-inkSoft mb-1">
              {isConsultation ? "Course or subject of interest" : "Course name"}
            </div>
            <input value={form.interest} onChange={(e) => set("interest", e.target.value)}
              placeholder={isConsultation ? "e.g. Python, Kids Coding, DevOps…" : courseName || "Course name"}
              className="w-full bg-eduhu-bg2 border border-eduhu-border px-3 py-2.5 text-sm focus:outline-none focus:border-eduhu-dark" />
          </label>

          {isConsultation && (
            <label className="block">
              <div className="overline text-eduhu-inkSoft mb-1">Message (optional)</div>
              <textarea value={form.message} onChange={(e) => set("message", e.target.value)}
                rows={3} placeholder="Tell us a bit about your learning goals…"
                className="w-full bg-eduhu-bg2 border border-eduhu-border px-3 py-2.5 text-sm focus:outline-none focus:border-eduhu-dark resize-none" />
            </label>
          )}

          {err && <p className="text-xs text-eduhu-error">{err}</p>}

          <button disabled={busy} className="eduhu-btn-primary w-full justify-center flex items-center gap-2">
            <Send size={14} />
            {busy ? "Sending…" : isConsultation ? "Book my free consultation" : "Send me the syllabus"}
          </button>
          <p className="text-xs text-center text-eduhu-inkSoft">
            We respect your privacy. No spam, ever.
          </p>
        </form>
      )}
    </Modal>
  );
}
